import { Button, Dialog } from '@mui/material';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Currencies from '../../../../utils/Currencies';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import { getPriceBasis } from 'arbitrip-common/client/utils/DealPriceBasis';

function TermsChangedDialog({
    modalIsOpen,
    onBack,
    onConfirm,
    priceChanged,
    oldPrice,
    newPrice,
    deadlineChanged,
    oldCancellationDeadline,
    newCancellationDeadline,
    boardChanged,
    oldBoard,
    newBoard,
    showUnchangedTerms,
    roomName,
    deal,
    nights,
    rooms,
    profile,
    t,
    i18n,
}) {
    const formatPrice = (price) =>
        Currencies.getPriceWithDisplayCurrencyByPrecision(
            price,
            profile.display_currency_exchange_rate,
            profile.display_currency,
            0,
        );

    const basis = getPriceBasis({ profile, deal, nights, rooms });
    const { show_total_price, toDisplayedPrice } = basis;
    const basisOldPrice = toDisplayedPrice(oldPrice);
    const basisNewPrice = toDisplayedPrice(newPrice);

    const exchangeRate = profile.display_currency_exchange_rate || 1;
    const displayedOld = Math.round(basisOldPrice * exchangeRate);
    const displayedNew = Math.round(basisNewPrice * exchangeRate);
    const isPriceIncrease = priceChanged && displayedNew > displayedOld;
    const displayedDelta = priceChanged ? displayedNew - displayedOld : 0;
    const sign = displayedDelta > 0 ? '+' : displayedDelta < 0 ? '-' : '';
    const formattedDelta = priceChanged && displayedDelta !== 0
        ? `${sign}${formatPrice(Math.abs(displayedDelta) / exchangeRate)}`
        : null;

    const hasVisiblePriceChange = priceChanged && displayedDelta !== 0;

    const priceBasisLabel = !basis.nights
        ? null
        : show_total_price
          ? t('TermsChangedDialog.TotalForNights', { count: basis.nights })
          : t('TermsChangedDialog.PerNight');
    const renderPriceBasis = () =>
        priceBasisLabel ? <span className="terms-changed-price-basis">{priceBasisLabel}</span> : null;

    const newTermsDeadlineTime = newCancellationDeadline ? new Date(newCancellationDeadline).getTime() : NaN;
    const newTermsRefundable = Number.isFinite(newTermsDeadlineTime) && newTermsDeadlineTime > Date.now();
    const disclosesCancellation = !!showUnchangedTerms || !!deadlineChanged;
    const newTermsRefundableForTest = !disclosesCancellation ? 'unknown' : newTermsRefundable ? 'true' : 'false';

    const formatDeadline = (deadline) => {
        const formatted = DateUtils.formatCancellationDeadline(deadline, i18n && i18n.language);
        return formatted
            ? t('TermsChangedDialog.FreeCancelUntil', { date: formatted })
            : t('ReservationDialog.NonRefundable');
    };

    const renderUnchangedValue = (value) => (
        <div className="terms-changed-value-row">
            <span className="terms-changed-value">{value}</span>
            <span className="terms-changed-unchanged-tag">{t('TermsChangedDialog.Unchanged')}</span>
        </div>
    );

    const renderChangedValues = (old_value, new_value) => (
        <div className="terms-changed-deadline-row">
            <div className="terms-changed-deadline-col old">
                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.Old')}</span>
                <span className="terms-changed-deadline-value">{old_value}</span>
            </div>
            <div className="terms-changed-arrow" />
            <div className="terms-changed-deadline-col new">
                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.New')}</span>
                <span className="terms-changed-deadline-value">{new_value}</span>
            </div>
        </div>
    );

    const cards = [
        {
            test_id: 'terms-changed-price',
            changed: hasVisiblePriceChange,
            label: !hasVisiblePriceChange
                ? t('TermsChangedDialog.Price')
                : isPriceIncrease
                  ? t('TermsChangedDialog.PriceIncreased')
                  : t('TermsChangedDialog.PriceDecreased'),
            unchanged_value: (
                <>
                    {formatPrice(basisNewPrice)}
                    {renderPriceBasis()}
                </>
            ),
            changed_body: (
                <div className="terms-changed-prices">
                    <div className="terms-changed-price-col old">
                        <span className="terms-changed-price-value">{formatPrice(basisOldPrice)}</span>
                        {renderPriceBasis()}
                    </div>
                    <div className="terms-changed-arrow" />
                    <div className="terms-changed-price-new-group">
                        <div className="terms-changed-price-col new">
                            <span className="terms-changed-price-value">{formatPrice(basisNewPrice)}</span>
                            {renderPriceBasis()}
                        </div>
                        <span className={`terms-changed-delta ${isPriceIncrease ? 'increase' : 'decrease'}`}>
                            {formattedDelta}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            test_id: 'terms-changed-cancellation',
            changed: !!deadlineChanged,
            label: deadlineChanged
                ? t('TermsChangedDialog.CancellationTermsChanged')
                : t('TermsChangedDialog.CancellationTerms'),
            unchanged_value: formatDeadline(newCancellationDeadline),
            changed_body: renderChangedValues(
                formatDeadline(oldCancellationDeadline),
                formatDeadline(newCancellationDeadline),
            ),
        },
        {
            test_id: 'terms-changed-extras',
            changed: !!boardChanged,
            label: boardChanged ? t('TermsChangedDialog.ExtrasChanged') : t('TermsChangedDialog.Extras'),
            unchanged_value: t(newBoard),
            changed_body: renderChangedValues(t(oldBoard), t(newBoard)),
        },
    ];

    // If nothing meaningful changed at display precision, skip the dialog entirely.
    React.useEffect(() => {
        if (modalIsOpen && !hasVisiblePriceChange && !deadlineChanged && !boardChanged) {
            onConfirm();
        }
    }, [modalIsOpen, hasVisiblePriceChange, deadlineChanged, boardChanged]);

    return (
        <Dialog
            className="reservation-dialog-main"
            open={modalIsOpen}
            aria-describedby="terms-changed-dialog-description"
            fullWidth={true}
        >
            <div
                className="terms-changed-container"
                data-testid="terms-changed-dialog"
                data-new-terms-refundable-for-test={newTermsRefundableForTest}
            >
                <div className="terms-changed-header">
                    <h3 className="terms-changed-title">{t('TermsChangedDialog.Title')}</h3>
                    {roomName ? (
                        <div className="terms-changed-room-name" data-testid="terms-changed-room-name">
                            {roomName}
                        </div>
                    ) : null}
                </div>

                <div className="terms-changed-cards">
                    {cards
                        .filter(({ changed }) => showUnchangedTerms || changed)
                        .map(({ test_id, label, changed, changed_body, unchanged_value }) => (
                            <div className="terms-changed-card" key={test_id} data-testid={test_id}>
                                <span className="terms-changed-card-label">{label}</span>
                                {changed ? changed_body : renderUnchangedValue(unchanged_value)}
                            </div>
                        ))}
                </div>

                <div className="terms-changed-actions">
                    <Button
                        className="terms-changed-btn-back"
                        data-testid="terms-changed-back-button"
                        variant="outlined"
                        onClick={onBack}
                    >
                        {t('ReservationDialog.BackToHotelButton')}
                    </Button>
                    <Button
                        className="terms-changed-btn-confirm"
                        data-testid="terms-changed-confirm-button"
                        variant="contained"
                        onClick={onConfirm}
                    >
                        {hasVisiblePriceChange
                            ? t('TermsChangedDialog.ConfirmContinueWithPrice', { price: formatPrice(basisNewPrice) })
                            : t('ReservationDialog.ConfirmContinueButton')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

TermsChangedDialog.propTypes = {
    modalIsOpen: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    priceChanged: PropTypes.bool,
    oldPrice: PropTypes.number,
    newPrice: PropTypes.number,
    deadlineChanged: PropTypes.bool,
    oldCancellationDeadline: PropTypes.string,
    newCancellationDeadline: PropTypes.string,
    boardChanged: PropTypes.bool,
    oldBoard: PropTypes.string,
    newBoard: PropTypes.string,
    showUnchangedTerms: PropTypes.bool,
    roomName: PropTypes.string,
    deal: PropTypes.object,
    nights: PropTypes.number,
    rooms: PropTypes.number,
    profile: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape({ language: PropTypes.string }),
};

export default memo(withTranslation()(TermsChangedDialog));
